<script setup lang="ts">
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpBtn } from '@gportal/uikit';
import { type UploadPlugin } from '@panel/plugins/types';
import { computed, getCurrentInstance, onUnmounted, ref, toRef, watchEffect } from 'vue';

// for some reason inject doesn't work here (vue toastitication may not support provide-inject, since it uses an older version of vue 3?)
const instance = getCurrentInstance();
const uploadPlugin = instance.appContext.app.config.globalProperties.$upload as UploadPlugin;

const isUploading = toRef(uploadPlugin, 'isUploading');
const uploadProgress = toRef(uploadPlugin, 'uploadProgress');
const uploadError = toRef(uploadPlugin, 'error');

const emit = defineEmits<{ dismiss: [] }>();

// cancel upload confirmation
const isConfirmingCancel = ref(false);
const cancelUpload = () => {
  uploadPlugin.cancelUpload();
  emit('dismiss');
};

const isDoneUploading = ref(false);
const uploadHasError = computed(() => uploadError.value != null && !isUploading.value);
const timeoutId = ref<number | null>(null);
const handleUploadStop = (timeout = 5000) => {
  timeoutId.value = setTimeout(() => {
    emit('dismiss');
  }, timeout);
};

watchEffect(() => {
  // handle when upload has been stopped
  if (!isUploading.value) {
    // if the upload has been stopped because of an error, show the error, otherwise show the success message
    isDoneUploading.value = !uploadHasError.value;
    console.log('isDoneUploading', isDoneUploading.value, uploadHasError.value);

    if (uploadHasError.value) {
      handleUploadStop(10000);
    } else {
      handleUploadStop();
    }
  }
});

onUnmounted(() => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
  }
});
</script>

<template>
  <div class="tw-min-w-[21rem]">
    <transition name="fade" mode="out-in">
      <template v-if="isUploading">
        <!-- confirmation for cancel -->
        <div v-if="isConfirmingCancel">
          <h3 class="tw-mb-2 tw-text-base tw-font-bold">Uploading image file ...</h3>
          <p class="tw-mb-2">Are you sure you want to cancel the upload?</p>
          <div class="tw-flex tw-justify-end tw-gap-2">
            <GpBtn label="No" btn-type="cancel" size="sm" @click="isConfirmingCancel = false" />
            <GpBtn label="Yes" btn-type="danger" size="sm" @click="cancelUpload" />
          </div>
        </div>
        <!-- upload progress -->
        <div v-else>
          <h3 class="tw-mb-2 tw-text-base tw-font-bold">Uploading image file ...</h3>
          <p>
            <FontAwesomeIcon :icon="faCircleNotch" class="fa-spin tw-mr-2" />
            Progress: {{ uploadProgress }}%
          </p>
          <div class="tw-mt-2 tw-flex tw-justify-end">
            <GpBtn
              label="Cancel"
              btn-type="danger"
              outline
              size="sm"
              @click="isConfirmingCancel = true"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <!-- upload error -->
        <div v-if="uploadHasError">
          <h3 class="tw-mb-2 tw-text-base tw-font-bold tw-text-gp-fire">Upload failed!</h3>
          <p>{{ uploadError }}</p>
        </div>
        <!-- upload complete -->
        <h3 v-else class="tw-text-base tw-font-bold">Upload completed.</h3>
      </template>
    </transition>
  </div>
</template>
